import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { AuthLegalLine } from "@/components/AuthLegalLine";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      setSent(true);
      toast.success(data.message || "Check your inbox.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong.");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="max-w-md mx-auto px-6 pt-32 pb-16" data-testid="forgot-password-page">
      <div className="card-elegant p-8">
        <h1 className="font-serif text-3xl">Forgot your password?</h1>
        <p className="text-sm text-[color:var(--text-muted)] mt-2">Enter your email and we'll send reset instructions.</p>
        {!sent ? (
          <form onSubmit={submit} className="space-y-4 mt-6">
            <Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 bg-white" />
            <Button data-testid="forgot-submit-btn" type="submit" disabled={submitting} className="rounded-full h-11 w-full bg-[#1A1A1A] text-white">{submitting ? "Sending…" : "Send reset link"}</Button>
          </form>
        ) : (
          <div className="mt-6 text-sm text-[color:var(--text-muted)]" data-testid="forgot-generic-msg">
            If an account exists for this email, reset instructions have been sent.
          </div>
        )}
        <div className="mt-6 text-sm"><Link to="/login" className="link-underline">Back to sign in</Link></div>
      </div>
      <AuthLegalLine className="mt-4 text-center" />
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setSubmitting(true);
    try {
      await api.post("/auth/password/reset", { token, new_password: pw });
      setOk(true);
      toast.success("Password updated. You can sign in now.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 pt-32 pb-16" data-testid="reset-password-page">
      <div className="card-elegant p-8">
        <h1 className="font-serif text-3xl">Choose a new password</h1>
        {!ok ? (
          <form onSubmit={submit} className="space-y-4 mt-6">
            <Input data-testid="reset-pw-input" type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="h-11 bg-white" />
            <Input data-testid="reset-pw2-input" type="password" minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" className="h-11 bg-white" />
            <Button data-testid="reset-submit-btn" type="submit" disabled={submitting || !token} className="rounded-full h-11 w-full bg-[#1A1A1A] text-white">{submitting ? "Updating…" : "Update password"}</Button>
            {!token && <div className="text-xs text-[#D9583A]">Missing reset token. Please use the link from your email.</div>}
          </form>
        ) : (
          <div className="mt-6 text-sm" data-testid="reset-success">
            Your password has been updated. <Link to="/login" className="link-underline">Sign in</Link>.
          </div>
        )}
      </div>
      <AuthLegalLine className="mt-4 text-center" />
    </div>
  );
}
